import React, { CSSProperties, FC, MutableRefObject, ReactNode, useRef } from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import { PerformerOut, ProductCopyDetails } from '../../store/api';
import { CountryOut, CreditRecordOut } from '../../store/apiLibx';

const StTableWrapper = styled.section<{ cellPaddingVertical: number }>`
    position: relative;
    padding-top: ${props => (props.cellPaddingVertical ? `calc(1em + 2 * ${props.cellPaddingVertical}px)` : '1em')};
`;

const StTableContainer = styled.div<{ maxHeight?: number; minHeight?: number }>`
    overflow-y: auto;
    max-height: ${props => (props.maxHeight ? `${props.maxHeight}px` : '100px')};
    min-height: ${props => (props.minHeight ? `${props.minHeight}px` : null)};
`;

const StTable = styled.table`
    border-spacing: 0;
    width: 100%;
`;

const StHeaderCell = styled.th<{ cellPaddingHorizontal: number }>`
    height: 0;
    line-height: 0;
    color: transparent;
    border: none;
    white-space: nowrap;
    overflow-y: hidden;
    padding: ${props => `0 ${props.cellPaddingHorizontal ? props.cellPaddingHorizontal : 0}px`};
    user-select: none;

    :first-of-type {
        padding-left: 0;
    }

    :last-of-type {
        padding-right: 0;
    }
`;

const StFixedHeaderCell = styled.div<{ cellPaddingVertical: number }>`
    position: absolute;
    background: transparent;
    top: 0;
    line-height: normal;
    color: black;
    padding: ${props => `${props.cellPaddingVertical ? props.cellPaddingVertical : 0}px 0`};
    user-select: text;
`;

const StCell = styled.td<{ cellPaddingVertical: number; cellPaddingHorizontal: number }>`
    padding: ${props =>
        `${props.cellPaddingVertical ? props.cellPaddingVertical : 0}px ${
            props.cellPaddingHorizontal ? props.cellPaddingHorizontal : 0
        }px`};
    vertical-align: middle;

    :first-of-type {
        padding-left: 0;
    }

    :last-of-type {
        padding-right: 0;
    }
`;

type AccessorProps = {
    orderNumber?: string;
    invoice?: string;
    shipDate?: number;
    startDate?: number;
    expiryDate?: number;
    endDate?: number;
    usageConditions?: string;
    planEndDate?: number;
    planStartDate?: number;
    issueTitle?: string;
    biography?: string;
    country?: string;
    cost?: number;
    creditAmount?: number;
    purchaseDate?: number;
    date?: number;
} & ProductCopyDetails;

interface Props {
    data: ProductCopyDetails[] | CreditRecordOut[] | PerformerOut[] | CountryOut[] | undefined;
    columns: {
        label: string;
        accessor: ((copy: AccessorProps) => string | number | ReactNode) | string;
        style?: any;
    }[];
    className?: string;
    headerStyle?: object;
    cellPaddingVertical: number;
    cellPaddingHorizontal: number;
    maxHeight?: number;
    minHeight?: number;
    getRowStyle?: (copy: AccessorProps) => CSSProperties;
}

export const DataTable: FC<Props> = ({
    cellPaddingHorizontal,
    cellPaddingVertical,
    className,
    columns,
    data,
    getRowStyle,
    headerStyle,
    maxHeight,
    minHeight
}) => {
    const tableOverflowWrapper = useRef() as MutableRefObject<HTMLDivElement>;
    const classes = classNames(className);

    return (
        <StTableWrapper cellPaddingVertical={cellPaddingVertical} className={classes}>
            <StTableContainer maxHeight={maxHeight} minHeight={minHeight} ref={tableOverflowWrapper}>
                <StTable>
                    <thead>
                        <tr>
                            {columns.map((column, index) => (
                                <StHeaderCell
                                    cellPaddingHorizontal={cellPaddingHorizontal}
                                    key={`table-header-${index}`}
                                    style={headerStyle}
                                >
                                    {column.label}
                                    <StFixedHeaderCell cellPaddingVertical={cellPaddingVertical}>
                                        {column.label}
                                    </StFixedHeaderCell>
                                </StHeaderCell>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data?.map((dataObj, rowIndex) => (
                            <tr key={`table-row-${rowIndex}`} style={getRowStyle ? getRowStyle(dataObj) : undefined}>
                                {columns.map((column, columnIndex) => (
                                    <StCell
                                        key={`table-cell-${columnIndex}`}
                                        style={column.style}
                                        cellPaddingVertical={cellPaddingVertical}
                                        cellPaddingHorizontal={cellPaddingHorizontal}
                                    >
                                        {typeof column.accessor === 'function'
                                            ? column.accessor(dataObj)
                                            : dataObj[column.accessor]}
                                    </StCell>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </StTable>
            </StTableContainer>
        </StTableWrapper>
    );
};
